import { HelpBanner } from "@faro-lotv/flat-ui";
import { SxProps, Theme } from "@mui/material";
import type { JSX } from "react";
import { AvailableAnnotationTools } from "../../r3f/renderers/annotations/annotations-types";
import {
  AnalysisHelpBanner,
  CreateAnnotationHelpBanner,
  EditWaypointsHelpBanner,
  MeasurementHelpBanner,
} from "./help-banners";

type BannerProps = {
  /** True if the tool help banner should be enabled */
  enabled?: boolean;

  /** Additional style to the help banner */
  sx?: SxProps<Theme>;
};

interface MeasureHelpBannerProps extends BannerProps {
  /** True if depth ifo are being loaded and prevents further measurement */
  cannotMeasureBecauseWaitingForDepths?: boolean;
}

interface AnnotationHelpBannerProps extends BannerProps {
  /** Currently available type of annotation tool */
  availableAnnotationTool?: AvailableAnnotationTools;
}

type Props = {
  /** The props which controls the behavior of measure help banner */
  measureHelpBanner?: MeasureHelpBannerProps;

  /** The props which controls the behavior of annotation help banner */
  annotationHelpBanner?: AnnotationHelpBannerProps;
};

/** Shared styles by all ToolsHelpBanners */
const COMMON_HELP_BANNER_SX = {
  justifyContent: "center",
  alignSelf: "center",
};

/** @returns the help banners required for the tools in the current mode */
export function ToolsHelpBanners({
  measureHelpBanner = {},
  annotationHelpBanner = {},
}: Props): JSX.Element {
  const {
    enabled: measureEnabled = true,
    cannotMeasureBecauseWaitingForDepths = false,
  } = measureHelpBanner;

  const {
    enabled: annotationEnabled = true,
    availableAnnotationTool = AvailableAnnotationTools.singlePoint,
  } = annotationHelpBanner;

  return (
    <>
      {cannotMeasureBecauseWaitingForDepths && (
        <HelpBanner sx={COMMON_HELP_BANNER_SX}>
          Measurement in progress. Loading additional data.
        </HelpBanner>
      )}
      {measureEnabled && (
        <MeasurementHelpBanner
          sx={{
            ...COMMON_HELP_BANNER_SX,
            ...measureHelpBanner.sx,
          }}
        />
      )}

      {annotationEnabled && (
        <CreateAnnotationHelpBanner
          availableTool={availableAnnotationTool}
          sx={{
            ...COMMON_HELP_BANNER_SX,
            ...annotationHelpBanner.sx,
          }}
        />
      )}

      <AnalysisHelpBanner sx={COMMON_HELP_BANNER_SX} />

      <EditWaypointsHelpBanner sx={COMMON_HELP_BANNER_SX} />
    </>
  );
}
